import { withStyles, WithStyles } from '@material-ui/core/styles';
import { FC, useEffect, useMemo, useState } from 'react';

import { Tooltip } from '@shared/components/tooltip';
import { isKeyPress } from '@shared/utils/browser-events';

import { styles } from './MultiGroupSelect.styles';
import { GroupSelect, GroupSelectProps, MultiGroupSelectOnChange } from '../GroupSelect';

export interface MultiGroupSelectProps
  extends Omit<GroupSelectProps, 'classes' | 'defaultValue' | 'multiple' | 'tooltip' | 'onChange' | 'value'>,
    WithStyles<typeof styles> {
  defaultValue?: Id[];
  saveOnClose?: boolean;
  value: Id[];
  // With saveOnClose: called when the menu closes without saving, which is on Escape (dropping the ticks made since
  // opening) or with nothing changed
  onCancel?: () => void;
  onChange?: MultiGroupSelectOnChange;
  classes: WithStyles<typeof styles>['classes'] & {
    groupItem?: string;
  };
}

const MultiGroupSelectComponent: FC<MultiGroupSelectProps> = ({
  saveOnClose,
  value,
  onCancel,
  onChange,
  ...groupSelectProps
}) => {
  const [menuOpen, setMenuOpen] = useState<boolean>(!!groupSelectProps.defaultOpen);
  const [selectedOptions, setSelectedOptions] = useState<Id[]>(() => value);

  useEffect(() => {
    setSelectedOptions(value);
  }, [value]);

  const selectedTooltipOptions = useMemo(() => {
    if (!selectedOptions.length) {
      return '';
    }
    return selectedOptions
      .reduce<Id[]>((result, item) => {
        groupSelectProps.groupOptions.forEach((group) => {
          const groupOption = group.options.find((option) => option.id === item);

          if (groupOption) {
            result.push(groupOption.label);
          }
        });

        return result;
      }, [])
      .join(', ');
  }, [groupSelectProps.groupOptions, selectedOptions]);

  const hasChanges = useMemo(() => {
    const selectedOptionsSet = new Set(selectedOptions);
    const valueSet = new Set(value);

    return selectedOptions.some((item) => !valueSet.has(item)) || value.some((item) => !selectedOptionsSet.has(item));
  }, [selectedOptions, value]);

  return (
    <Tooltip
      classes={{ container: groupSelectProps.classes.tooltip }}
      placement="top"
      title={menuOpen ? '' : selectedTooltipOptions}
    >
      <GroupSelect
        {...groupSelectProps}
        multiple
        value={selectedOptions}
        onChange={(value, option) => {
          setSelectedOptions(value as Id[]);

          if (!saveOnClose && onChange) {
            onChange(value as Id[], option);
          }
        }}
        onClose={(event) => {
          setMenuOpen(false);

          if (saveOnClose && (isKeyPress(event, 'Escape') || !hasChanges)) {
            // Not saving: Escape drops the ticks made since opening. A close with nothing changed still reports the
            // cancel, as an inline editor leaves edit mode only through onCancel or onChange
            setSelectedOptions(value);
            onCancel?.();
          } else if (saveOnClose && hasChanges && onChange) {
            // Saving: a click away with ticks changed
            onChange(selectedOptions, undefined);
          }
        }}
        onOpen={() => setMenuOpen(true)}
      />
    </Tooltip>
  );
};

export const MultiGroupSelect = withStyles(styles)(MultiGroupSelectComponent);
